import React, { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Cloud, Database, Camera, Server, ChevronsUpDown, ExternalLink, SquareArrowOutUpLeft, Zap, Volume2 } from 'lucide-react';
import { PlusCircle } from 'lucide-react';
import { useIntegrations } from '@/hooks/integrations';
import { useListConnections } from '@/hooks/externalConnectionsApi';
import { useToast } from '@/hooks/use-toast';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { PopoverTrigger } from '@/components/ui/popover';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ExternalConnectionType } from '@/lib/integrationTypes/externalConnectionTypes';
import { Link, useNavigate } from 'react-router-dom';
import { IntegrationLimitedDto } from '@/lib/integrationTypes/integrationTypes';
import { HELP_CENTER_LINKS } from '@/lib/onboardingTours/helpCenterLinks';

type SelectIntegrationTypeProps = {
  onChange: (opts: SelectedIntegration | undefined) => void;
};

type DeviceType = 'Nvr' | 'External Service' | 'VMS' | 'Relay' | 'Speaker';

export type SelectedIntegration =
  | { integration: IntegrationLimitedDto }
  | { externalConnection: string };

const DEVICE_TYPE_KEY = `ihub_${3}_deviceType`;

const getIconForType = (type: DeviceType) => {
  switch (type) {
    case 'Nvr':
      return Server;
    case 'VMS':
      return Cloud;
    case 'External Service':
      return Database;
    case 'Relay':
      return Zap;
    case 'Speaker':
      return Volume2;
    default:
      return Camera;
  }
};

const SelectIntegrationType = ({ onChange }: SelectIntegrationTypeProps) => {
  const existingLocal = localStorage.getItem(DEVICE_TYPE_KEY);

  const [selectedType, setSelectedType] = useState<DeviceType>('VMS');
  const [selectedDevice, setSelectedDevice] = useState<{ name: string; id: string } | undefined>();
  const [open, setOpen] = useState(false);
  const { data: integrations, error: integrationsError } = useIntegrations();
  const { data: externalConnections, error: externalConnectionsError } = useListConnections();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem(DEVICE_TYPE_KEY, selectedType);
  });

  useEffect(() => {
    if (integrationsError) {
      toast({
        title: 'Could not fetch external connections ' + integrationsError.message,
        variant: 'destructive',
        duration: 10,
      });
    }
  }, [integrationsError, toast]);

  useEffect(() => {
    if (externalConnectionsError) {
      toast({
        title: 'Could not fetch external connections ' + externalConnectionsError.message,
        variant: 'destructive',
        duration: 10,
      });
    }
  }, [externalConnectionsError, toast]);

  const possibleIntegrations =
    selectedType === 'Nvr'
      ? integrations?.integrations
          .filter(({ integrationType }) => integrationType === 'Nvr')
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ name, id }) => ({ name, id, integrationType: 'integration' as const }))
      : selectedType === 'Relay'
      ? integrations?.integrations
          .filter(({ integrationType }) => integrationType === 'Relay')
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ name, id }) => ({ name, id, integrationType: 'integration' as const }))
      : selectedType === 'Speaker'
      ? integrations?.integrations
          .filter(({ integrationType }) => integrationType === 'Speaker')
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ name, id }) => ({ name, id, integrationType: 'integration' as const }))
      : externalConnections
          ?.filter(
            ({ type }) =>
              type ===
              (selectedType === 'VMS'
                ? ExternalConnectionType.Vms
                : ExternalConnectionType.ExternalService)
          )
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ name, id }) => ({
            name,
            id: id!,
            integrationType: 'externalConnection' as const,
          }));

  return (
    <>
      <div className="mt-6 grid flex-1 items-center gap-1.5" data-onboarding="add-device-integration-type">
        <div className="mt-2 grid grid-cols-4 gap-4">
          <label
            className={`flex flex-col cursor-pointer rounded-lg border p-4 ${
              selectedType === 'VMS' ? 'border-lime bg-lime/10' : 'border-gray-700'
            }`}
            onClick={() => {
              if (selectedType !== 'VMS') {
                setSelectedType('VMS');
                onChange(undefined);
                setSelectedDevice(undefined);
              }
            }}
          >
            <div className="flex w-full items-center">
              <input
                type="radio"
                name="deviceType"
                checked={selectedType === 'VMS'}
                onChange={() => {}}
                className="sr-only"
              />
              <span className="font-semibold text-lime">Cloud VMS</span>
              <Cloud className="ml-auto text-lime" />
            </div>
            <div className="mt-2 text-xs text-gray-400">
              To use a VMS, first register it on the{' '}
              <Link to="/connections" className="text-lime hover:underline">
                integrations page
              </Link>
              . Once registered, it will be available for reuse when adding devices.
            </div>
          </label>
          <label
            className={`flex flex-col cursor-pointer rounded-lg border p-4 ${
              selectedType === 'Nvr' ? 'border-lime bg-lime/10' : 'border-gray-700'
            }`}
            onClick={() => {
              if (selectedType !== 'Nvr') {
                setSelectedType('Nvr');
                onChange(undefined);
                setSelectedDevice(undefined);
              }
            }}
          >
            <div className="flex w-full items-center">
              <input
                type="radio"
                name="deviceType"
                checked={selectedType === 'Nvr'}
                onChange={() => {}}
                className="sr-only"
              />
              <span className="font-semibold text-lime">NVR/Camera</span>
              <Database className="ml-auto text-lime" />
            </div>
            <div className="mt-2 text-xs text-gray-400">
              Use this to connect directly to edge hardware.
            </div>
          </label>
          <label
            className={`flex flex-col cursor-pointer rounded-lg border p-4 ${
              selectedType === 'Speaker' ? 'border-lime bg-lime/10' : 'border-gray-700'
            }`}
            onClick={() => {
              if (selectedType !== 'Speaker') {
                setSelectedType('Speaker');
                onChange(undefined);
                setSelectedDevice(undefined);
              }
            }}
          >
            <div className="flex w-full items-center">
              <input
                type="radio"
                name="deviceType"
                checked={selectedType === 'Speaker'}
                onChange={() => {}}
                className="sr-only"
              />
              <span className="font-semibold text-lime">Speaker/Horn</span>
              <Volume2 className="ml-auto text-lime" />
            </div>
            <div className="mt-2 text-xs text-gray-400">
              Use this to connect speaker devices for audio operations.
            </div>
          </label>
          <label
            className={`flex flex-col cursor-pointer rounded-lg border p-4 ${
              selectedType === 'Relay' ? 'border-lime bg-lime/10' : 'border-gray-700'
            }`}
            onClick={() => {
              if (selectedType !== 'Relay') {
                setSelectedType('Relay');
                onChange(undefined);
                setSelectedDevice(undefined);
              }
            }}
          >
            <div className="flex w-full items-center">
              <input
                type="radio"
                name="deviceType"
                checked={selectedType === 'Relay'}
                onChange={() => {}}
                className="sr-only"
              />
              <span className="font-semibold text-lime">Relay</span>
              <Zap className="ml-auto text-lime" />
            </div>
            <div className="mt-2 text-xs text-gray-400">
              Use this to connect relay devices for control operations.
            </div>
          </label>
        </div>
      </div>
      <div className="mt-6 grid flex-1 items-center gap-1.5">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between text-white"
            >
              {selectedDevice ? (
                <span className="flex items-center gap-2">
                  {React.createElement(getIconForType(selectedType), {
                    className: 'h-4 w-4',
                  })}
                  {selectedDevice.name}
                </span>
              ) : (
                selectedType === 'VMS' ? 'Select connection...' : 'Select Device...'
              )}
              <ChevronsUpDown className="text-lime" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[400px] p-0" align="start">
            <Command>
              <CommandInput placeholder={selectedType === 'VMS' ? 'Search connections...' : 'Search devices...'} className="h-10" />
              <CommandList className="max-h-[300px]">
                <CommandEmpty>
                  {selectedType === 'VMS'
                    ? 'A VMS can be registered globally in the integrations page and then re-used when adding devices.'
                    : 'No devices found.'}
                </CommandEmpty>
                <CommandGroup>
                  {selectedType === 'VMS' && (
                    <CommandItem
                      onSelect={() => {
                        navigate('/connections');
                      }}
                    >
                      <PlusCircle className="mr-2 h-4 w-4" /> Add a new VMS
                    </CommandItem>
                  )}
                  {possibleIntegrations?.map(({ name, integrationType, id }) => (
                    <CommandItem
                      key={id}
                      value={JSON.stringify({ integrationType, id })}
                      keywords={[name]}
                      onSelect={() => {
                        setSelectedDevice({ name, id });
                        switch (integrationType) {
                          case 'integration':
                            onChange({
                              integration: integrations!.integrations.find(
                                ({ id: x }) => x === id
                              )!,
                            });
                            break;
                          case 'externalConnection':
                            onChange({ externalConnection: id });
                            break;
                        }
                        setOpen(false);
                      }}
                    >
                      {React.createElement(getIconForType(selectedType), {
                        className: 'mr-2 h-4 w-4',
                      })}
                      {name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
              <Link
                target="_blank"
                to={HELP_CENTER_LINKS.contactUs}
                className={`rounded-none border-b-0 border-l-0 border-r-0 ${buttonVariants({ variant: 'outline' })}`}
              >
                Request a new integration
              </Link>
            </Command>
          </PopoverContent>
        </Popover>
      </div>{' '}
    </>
  );
};

export default SelectIntegrationType;
